import { useState } from 'react';
import { format } from 'date-fns';
import {
  BarChart3,
  FileDown,
  RefreshCw,
  TrendingUp,
  Users,
  DollarSign,
  Star,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import {
  useReportsDashboard,
  useReportSnapshots,
  useGenerateReport,
} from '../hooks/useApi';
import { api } from '../api/client';
import type { ReportType } from '../types';

const reportTypeLabels: Record<ReportType, string> = {
  daily_summary: 'Daily Summary',
  weekly_digest: 'Weekly Digest',
  job_performance: 'Job Performance',
  model_comparison: 'Model Comparison',
};

export default function Reports() {
  const [days, setDays] = useState(7);
  const [generateType, setGenerateType] = useState<ReportType>('daily_summary');
  const [isGenerating, setIsGenerating] = useState(false);
  const [snapshotsPage, setSnapshotsPage] = useState(1);

  const { data: analytics, isLoading: analyticsLoading } = useReportsDashboard(days);
  const { data: snapshots, isLoading: snapshotsLoading, refetch: refetchSnapshots } = useReportSnapshots(snapshotsPage, 10);
  const generateReport = useGenerateReport();

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      await generateReport.mutateAsync({
        reportType: generateType,
        startDate,
        endDate,
      });
      refetchSnapshots();
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportCsv = async (snapshotId: number) => {
    try {
      const blob = await api.exportReportCsv(snapshotId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${snapshotId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently handle errors
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Analytics and report generation for recruitment data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {analyticsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-lg shadow p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      ) : analytics ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            icon={<Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
            label="Applications"
            value={analytics.summary.totalApplications}
            bgColor="bg-blue-50 dark:bg-blue-900/20"
          />
          <SummaryCard
            icon={<BarChart3 className="h-5 w-5 text-green-600 dark:text-green-400" />}
            label="Reviews"
            value={analytics.summary.totalReviews}
            bgColor="bg-green-50 dark:bg-green-900/20"
          />
          <SummaryCard
            icon={<Star className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
            label="Avg Score"
            value={analytics.summary.averageScore.toFixed(1)}
            bgColor="bg-amber-50 dark:bg-amber-900/20"
          />
          <SummaryCard
            icon={<DollarSign className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
            label="Total Cost"
            value={`$${analytics.summary.totalCost.toFixed(4)}`}
            bgColor="bg-purple-50 dark:bg-purple-900/20"
          />
        </div>
      ) : null}

      {/* Charts Row */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Score Distribution */}
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Score Distribution</h3>
            <div className="space-y-2">
              {Object.entries(analytics.scoreDistribution).map(([bucket, count]) => {
                const maxCount = Math.max(...Object.values(analytics.scoreDistribution), 1);
                const pct = (count / maxCount) * 100;
                return (
                  <div key={bucket} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-10">{bucket}</span>
                    <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Jobs */}
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Top Jobs</h3>
            {analytics.topJobs.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">No job data in this period</p>
            ) : (
              <div className="space-y-3">
                {analytics.topJobs.map((job) => (
                  <div key={job.jobId} className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{job.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {job.applicationCount} apps | avg score {job.avgScore.toFixed(1)}
                      </p>
                    </div>
                    <TrendingUp className="h-4 w-4 text-gray-400 dark:text-gray-600 ml-2 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Trend */}
      {analytics && analytics.dailyTrend.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Daily Trend</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="pb-2 pr-4 font-medium">Date</th>
                  <th className="pb-2 pr-4 font-medium text-right">Applications</th>
                  <th className="pb-2 pr-4 font-medium text-right">Reviews</th>
                  <th className="pb-2 font-medium text-right">Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {analytics.dailyTrend.map((day) => (
                  <tr key={day.date} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{day.date}</td>
                    <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">{day.applications}</td>
                    <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">{day.reviews}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">{day.avgScore?.toFixed(1) ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Model Breakdown */}
      {analytics && analytics.modelBreakdown.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Model Usage</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="pb-2 pr-4 font-medium">Model</th>
                  <th className="pb-2 pr-4 font-medium text-right">Reviews</th>
                  <th className="pb-2 font-medium text-right">Avg Cost</th>
                </tr>
              </thead>
              <tbody>
                {analytics.modelBreakdown.map((m) => (
                  <tr key={m.model} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4 text-gray-900 dark:text-gray-100 font-mono text-xs">{m.model}</td>
                    <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">{m.count}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">${m.avgCost.toFixed(6)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generate Report Section */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Generate Report
        </h3>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Report Type</label>
            <div className="relative">
              <select
                value={generateType}
                onChange={(e) => setGenerateType(e.target.value as ReportType)}
                className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 pr-8 text-sm text-gray-900 dark:text-gray-100 appearance-none"
              >
                {Object.entries(reportTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGenerating ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <BarChart3 className="h-4 w-4" />
            )}
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>

      {/* Past Snapshots */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Past Reports</h3>
        {snapshotsLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
            ))}
          </div>
        ) : snapshots && snapshots.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="pb-2 pr-4 font-medium">ID</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Generated</th>
                    <th className="pb-2 pr-4 font-medium">Period</th>
                    <th className="pb-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.data.map((snap) => (
                    <tr key={snap.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">#{snap.id}</td>
                      <td className="py-2 pr-4">
                        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                          {reportTypeLabels[snap.reportType as ReportType] || snap.reportType}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">
                        {format(new Date(snap.generatedAt), 'MMM d, yyyy HH:mm')}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-gray-400 text-xs">
                        {snap.periodStart && snap.periodEnd
                          ? `${format(new Date(snap.periodStart), 'MMM d')} - ${format(new Date(snap.periodEnd), 'MMM d')}`
                          : '-'}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => handleExportCsv(snap.id)}
                          className="inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          <FileDown className="h-3 w-3" />
                          CSV
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {snapshots.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Page {snapshots.pagination.page} of {snapshots.pagination.totalPages} ({snapshots.pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={snapshotsPage <= 1}
                    onClick={() => setSnapshotsPage((p) => Math.max(1, p - 1))}
                    className="rounded-md border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    disabled={snapshotsPage >= snapshots.pagination.totalPages}
                    onClick={() => setSnapshotsPage((p) => p + 1)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No reports generated yet. Click "Generate" to create your first report.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function SummaryCard({
  icon,
  label,
  value,
  bgColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  bgColor: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-5">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${bgColor}`}>{icon}</div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </div>
    </div>
  );
}
